import { redirectRoute } from '@/router/helpers';
import { useUserStore } from '@/stores/user';
import unleash from '@/unleash';

const Register = () => import('@/modules/register/index.vue');
const Builder = () => import('@/modules/register/builder/index.vue');
const Payment = () => import('@/modules/register/builder/payment/index.vue');
const Sales = () => import('@/modules/register/sales/index.vue');
const DayOverview = () => import('@/modules/register/day-overview/index.vue');
const Drawer = () => import('@/modules/register/drawer/index.vue');
const DrawerClose = () => import('@/modules/register/drawer/CloseDrawer.vue');
const Cashups = () => import('@/modules/register/cashups/index.vue');
const CashupsZoom = () => import('@/modules/register/cashups/cashup/index.vue');
const GiftCards = () => import('@/modules/register/gift-cards/index.vue');
const PrepaidCards = () => import('@/modules/register/prepaid-cards/index.vue');
const SalonizedPay = () => import('@/modules/register/salonized-pay/index.vue');
const Sale = () => import('@/modals/sale/index.vue');
const InvoiceEmail = () => import('@/modals/invoice-email/index.vue');
const DocumentEmail = () => import('@/modals/sale-email/index.vue');
const DrawerTransaction = () =>
  import('@/modules/register/drawer/NewTransaction.vue');
const DrawerCurrentCash = () =>
  import('@/modules/register/drawer/EditCurrentCash.vue');
const GiftCard = () => import('@/modules/register/gift-cards/GiftCard.vue');
const PrepaidCard = () =>
  import('@/modules/register/prepaid-cards/PrepaidCard.vue');
const CreateGiftcard = () =>
  import('@/modules/register/builder/browser/pages/GiftCard.vue');
const CreatePrepaidcard = () =>
  import('@/modules/register/builder/browser/pages/PrepaidCard.vue');
const Services = () =>
  import('@/modules/register/builder/browser/pages/services/index.vue');
const Products = () =>
  import('@/modules/register/builder/browser/pages/products/index.vue');
const Appointments = () =>
  import('@/modules/register/builder/browser/pages/appointments/index.vue');

export default {
  path: 'register',
  name: 'register',
  component: Register,
  redirect: { name: 'register-builder' },
  meta: {
    moduleName: 'register',
    role: 'basic',
    disabledPlans: ['pro-light'],
  },
  children: [
    {
      path: 'checkout',
      name: 'register-builder',
      component: Builder,
      meta: {
        hideIntercomButton: true,
      },
      children: [
        {
          path: 'services',
          name: 'register-builder-services',
          component: Services,
        },
        {
          path: 'products',
          name: 'register-builder-products',
          component: Products,
        },
        {
          path: 'appointments',
          name: 'register-builder-appointments',
          component: Appointments,
        },
        {
          path: 'create-giftcard',
          name: 'register-builder-giftcard',
          component: CreateGiftcard,
          props: {
            parentRouteName: 'register-builder',
          },
        },
        {
          path: 'create-prepaidcard',
          name: 'register-builder-prepaidcard',
          component: CreatePrepaidcard,
          beforeEnter: () => {
            const { hasFeatureFlag } = useUserStore();
            if (
              unleash.isEnabled('EnableFiskalySign') &&
              hasFeatureFlag('fiskaly-sign')
            ) {
              return redirectRoute({ name: 'register-builder' });
            }
          },
        },
      ],
    },
    {
      path: 'payment',
      name: 'register-payment',
      component: Payment,
      meta: {
        hideIntercomButton: true,
      },
    },
    {
      path: 'sales',
      name: 'sales',
      component: Sales,
      children: [
        {
          path: ':id',
          name: 'sale',
          component: Sale,
          props: {
            parentRouteName: 'sales',
          },
        },
        {
          path: ':id/email',
          name: 'invoice-email',
          component: InvoiceEmail,
          props: {
            parentRouteName: 'sales',
          },
        },
        {
          path: ':orderId/document-email/:documentId',
          name: 'document-email',
          component: DocumentEmail,
          props: {
            parentRouteName: 'sales',
          },
        },
      ],
    },
    {
      path: 'invoices',
      redirect: { name: 'sales' },
    },
    {
      path: 'invoices/:id',
      redirect: (to) => ({ name: 'sale', params: to.params }),
    },
    {
      path: 'invoices/:id/email',
      redirect: (to) => ({ name: 'invoice-email', params: to.params }),
    },
    {
      path: 'invoices/:id/document-email/:documentId',
      redirect: (to) => ({ name: 'document-email', params: to.params }),
    },
    {
      path: 'day-overview',
      name: 'day-overview',
      component: DayOverview,
      children: [
        {
          path: 'day-overview/:id',
          name: 'day-overview-item',
          component: Sale,
          props: {
            parentRouteName: 'day-overview',
          },
        },
      ],
    },
    {
      path: 'drawer',
      name: 'drawer',
      component: Drawer,
      meta: {
        hideIntercomButton: true,
      },
      children: [
        {
          path: 'new-transaction',
          name: 'drawer-transaction',
          component: DrawerTransaction,
        },
        {
          path: 'change-current-cash',
          name: 'drawer-current-cash',
          component: DrawerCurrentCash,
        },
        {
          path: ':id',
          name: 'drawer-item',
          component: Sale,
          props: {
            parentRouteName: 'drawer',
          },
        },
      ],
    },
    {
      path: 'close-drawer',
      name: 'drawer-close',
      component: DrawerClose,
      meta: {
        hideIntercomButton: {
          mobile: true,
        },
      },
    },
    {
      path: 'cashups',
      name: 'cashups',
      component: Cashups,
    },
    {
      path: 'cashups/:id',
      name: 'cashup',
      component: CashupsZoom,
    },
    {
      path: 'salonized-pay',
      name: 'salonized-pay',
      component: SalonizedPay,
      meta: {
        unleash: 'SalonizedPay',
      },
    },
    {
      path: 'gift-cards',
      name: 'gift-cards',
      component: GiftCards,
      children: [
        {
          path: ':giftcardId',
          name: 'gift-card',
          component: GiftCard,
        },
      ],
    },
    {
      path: 'prepaid-cards',
      name: 'prepaid-cards',
      beforeEnter: () => {
        const { hasFeatureFlag } = useUserStore();
        if (
          unleash.isEnabled('EnableFiskalySign') &&
          hasFeatureFlag('fiskaly-sign')
        ) {
          return redirectRoute({ name: 'register-builder' });
        }
      },
      component: PrepaidCards,
      children: [
        {
          path: ':id',
          name: 'prepaid-card',
          component: PrepaidCard,
          props: {
            parentRouteName: 'prepaid-cards',
          },
        },
      ],
    },
  ],
};
